import React, { FC, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { BodyTextMedium, Button, HeadingText40, pxToRem } from '@modernhealth/vital-components';
import { useTranslation } from 'react-i18next';

import { navigationService } from 'src/config/rootService';
import { Answer } from 'src/modules/assessments/components/Answer';
import { useSubmitFeatureReviewMutation } from 'src/modules/providers/documents/submitFeatureReviewMutation.generated';
import { appClient } from 'src/config/apollo';
import { captureException } from '@sentry/browser';

const Wrapper = styled.div({
    display: 'flex',
    maxWidth: pxToRem(500),
    margin: '0 auto',
    height: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: pxToRem(100),
});

const Answers = styled('ul')({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    listStyleType: 'none',
    padding: 0,
    marginBottom: 0,
});

export enum RegistrationSurveyAnswers {
    ExploreMyBenefit = 'explore_my_benefit',
    ImproveMyWellbeing = 'improve_my_wellbeing',
    SpeakToProvider = 'speak_to_provider',
    InviteDependent = 'invite_dependent',
    DifferentReason = 'different_reason',
}

const answersList = [
    {
        answerTranslationKey: 'registration_survey_page.explore_my_benefit',
        value: RegistrationSurveyAnswers.ExploreMyBenefit,
    },
    {
        answerTranslationKey: 'registration_survey_page.improve_my_wellbeing',
        value: RegistrationSurveyAnswers.ImproveMyWellbeing,
    },
    {
        answerTranslationKey: 'registration_survey_page.speak_to_provider',
        value: RegistrationSurveyAnswers.SpeakToProvider,
    },
    {
        answerTranslationKey: 'registration_survey_page.invite_dependent',
        value: RegistrationSurveyAnswers.InviteDependent,
    },
    {
        answerTranslationKey: 'registration_survey_page.not_sure',
        value: RegistrationSurveyAnswers.DifferentReason,
    },
];

export const RegistrationSurveyPage: FC = () => {
    const { t } = useTranslation();
    const [answer, setAnswer] = useState('');

    const [submitFeatureReview, { error, loading }] = useSubmitFeatureReviewMutation({
        client: appClient,
    });

    const handleContinue = async () => {
        await submitFeatureReview({
            variables: {
                featureReview: {
                    feedbackChoice: answer,
                    featureRecordName: 'registration_survey',
                },
            },
        });
        navigationService.navigate('/start-assessment-page');
    };

    useEffect(() => {
        if (error) {
            captureException(error);
        }
    }, [error]);

    return (
        <Wrapper>
            <HeadingText40 marginBottom={12}>
                {t('auth:registration_survey_page.title')}
            </HeadingText40>
            <BodyTextMedium marginBottom={52}>
                {t('auth:registration_survey_page.subtitle')}
            </BodyTextMedium>
            <Answers>
                {answersList.map(a => (
                    <Answer
                        key={a.value}
                        isHighlighted={a.value === answer}
                        onClick={() => setAnswer(a.value)}
                    >
                        {t(`auth:${a.answerTranslationKey}`)}
                    </Answer>
                ))}
            </Answers>
            <Button
                variant="primary"
                onClick={handleContinue}
                disabled={!answer}
                fluid
                size="large"
                css={{ marginTop: 24 }}
                busy={loading}
            >
                {t('common.continue')}
            </Button>
        </Wrapper>
    );
};
